import React, { useState, useRef, useEffect, useCallback } from 'react'
import { Editor, Toolbar } from '@wangeditor/editor-for-react'
import { i18nChangeLanguage } from '@wangeditor/editor'
import '@wangeditor/editor/dist/css/style.css'

i18nChangeLanguage('en')

const AddNewsPage = ({ onBack, onSave }) => {
  const [title, setTitle] = useState('')
  const [mainImageFile, setMainImageFile] = useState(null)
  const [categoryId, setCategoryId] = useState('1')
  const [categories, setCategories] = useState([])
  const [loadingCategories, setLoadingCategories] = useState(true)
  const [editorHtml, setEditorHtml] = useState('')
  const [errors, setErrors] = useState({})
  const [editor, setEditor] = useState(null)
  const [addToTinker, setAddToTinker] = useState(false)
  const [priority, setPriority] = useState('عادي')  
  const [isPublishing, setIsPublishing] = useState(false)
  const [uploadingImages, setUploadingImages] = useState(0)

  const apiUrl = process.env.REACT_APP_API_URL;

  // Improved YouTube URL converter function
  const convertYouTubeUrls = (html) => {
    // More comprehensive regex to match various YouTube URL formats and remove surrounding HTML
    const youtubeRegex = /(?:<p[^>]*>)?\s*(?:<a[^>]*>)?(?:https?:\/\/)?(?:www\.)?(youtube\.com\/watch\?v=|youtu\.be\/)([a-zA-Z0-9_-]+)(?:[^\s<]*)?(?:<\/a>)?\s*(?:<\/p>)?/gi;
    
    return html.replace(youtubeRegex, (match, domain, videoId) => {
      return `<div style="margin: 20px 0; text-align: center;"><iframe width="560" height="315" src="https://www.youtube.com/embed/${videoId}" frameborder="0" allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture" allowfullscreen></iframe></div>`;
    });
  };

  const customUploadImg = useCallback(async (file, insertFn) => {
    try {
      setUploadingImages(prev => prev + 1)

      const compressedFile = await compressImage(file, 0.7, 1200) 
      
      if (compressedFile.size > 2 * 1024 * 1024) {
        alert('حجم الصورة كبير جداً حتى بعد الضغط. يرجى استخدام صورة أصغر')
        return
      }

      const formData = new FormData()
      formData.append('image', compressedFile)

      const response = await fetch(`${apiUrl}/api/upload-image`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${localStorage.getItem('authToken')}`
        },
        body: formData,
      })

      const data = await response.json()
      
      if (response.ok && data.success) {
        insertFn(data.url, compressedFile.name, data.url)
      } else {
        throw new Error(data.error || 'فشل في رفع الصورة')
      }
      
    } catch (error) {
      console.error('Upload error:', error)
      alert('فشل في رفع الصورة: ' + error.message)
    } finally {
      setUploadingImages(prev => prev - 1)
    }
  }, [apiUrl])

  const compressImage = (file, quality = 0.7, maxWidth = 1200) => {
    return new Promise((resolve) => {
      const canvas = document.createElement('canvas')
      const ctx = canvas.getContext('2d')
      const img = new Image()
      
      img.onload = () => {
        let { width, height } = img
        if (width > maxWidth) {
          height = (height * maxWidth) / width
          width = maxWidth
        }
        
        canvas.width = width
        canvas.height = height
        ctx.drawImage(img, 0, 0, width, height)
        canvas.toBlob(resolve, file.type, quality)
      }
      
      img.src = URL.createObjectURL(file)
    })
  }

  const editorConfig = {
    placeholder: 'اكتب محتوى الخبر هنا... (يمكنك لصق رابط يوتيوب مباشرة وسيتم تحويله تلقائياً)',
    MENU_CONF: {
      uploadImage: {
        customUpload: customUploadImg
      }
    },
    lang: 'en',
    customAlert: (s, t) => { alert(s) }
  }

  const toolbarConfig = {
    toolbarKeys: [
      'headerSelect','blockquote','|','bold','underline','italic','through','|',
      'color','bgColor','|','fontSize','|','bulletedList','numberedList',
      { 
        key: 'group-justify', 
        title: 'Align',
        iconSvg: '<svg viewBox="0 0 1024 1024"><path d="M768 793.6v102.4H51.2v-102.4h716.8z m204.8-230.4v102.4H51.2v-102.4h921.6z m-204.8-230.4v102.4H51.2v-102.4h716.8z m204.8-230.4v102.4H51.2v-102.4h921.6z"></path></svg>',
        menuKeys: ['justifyLeft','justifyRight','justifyCenter','justifyJustify']
      },
      '|','emotion','insertLink',
      { 
        key: 'group-image', 
        title: 'Image',
        iconSvg: '<svg viewBox="0 0 1024 1024"><path d="M959.877 128l0.123 0.123v767.775l-0.123 0.122H64.102l-0.122-0.122V128.123l0.122-0.123h895.775zM960 64H64C28.795 64 0 92.795 0 128v768c0 35.205 28.795 64 64 64h896c35.205 0 64-28.795 64-64V128c0-35.205-28.795-64-64-64zM832 288.01c0 53.023-42.988 96.01-96.01 96.01s-96.01-42.987-96.01-96.01S682.967 192 735.99 192 832 234.988 832 288.01zM896 832H128V704l224.01-384 256 320h64l224.01-192z"></path></svg>',
        menuKeys: ['uploadImage']
      },
      '|','insertTable','codeBlock','divider','|','undo','redo'
    ]
  }

  const fetchCategories = useCallback(async () => {
    try {
      setLoadingCategories(true)
      const response = await fetch(`${apiUrl}/api/categories`)
      const data = await response.json()

      if (response.ok && data.success) {
        setCategories(data.data)
      } else {
        setCategories([])
      }
    } catch (error) {
      setCategories([])
    } finally {
      setLoadingCategories(false)
    }
  }, [apiUrl])

  useEffect(() => {
    fetchCategories()
  }, [fetchCategories])

  const validateForm = () => {
    const newErrors = {}

    if (!title.trim()) newErrors.title = 'عنوان الخبر مطلوب'
    if (!editorHtml.trim() || editorHtml === '<p><br></p>') newErrors.content = 'محتوى الخبر مطلوب'
    
    if (uploadingImages > 0) {
      newErrors.content = 'يرجى انتظار انتهاء رفع الصور'
    }
    
    setErrors(newErrors)
    return Object.keys(newErrors).length === 0
  }

  const handleSubmit = async e => {
    e.preventDefault()
    if (!validateForm()) return
    setIsPublishing(true)

    const formData = new FormData()
    formData.append('title', title)
    // Convert YouTube URLs before sending
    formData.append('content', convertYouTubeUrls(editorHtml))
    formData.append('category_id', categoryId || '1')
    formData.append('priority', priority)
    formData.append('add_to_tinker', addToTinker ? '1' : '0')
    
    // ✅ REMOVED: Don't send published_at from frontend
    // Let the backend handle this naturally
    // The backend will use created_at for time calculations when published_at is null
    
    if (mainImageFile) formData.append('main_image', mainImageFile)

    try {
      const response = await fetch(`${apiUrl}/api/news`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${localStorage.getItem('authToken')}`
        },
        body: formData,
      })
      const data = await response.json()

      if (response.ok) {
        // Clear the draft after successful submission
        localStorage.removeItem('addNewsFormDraft')
        setTitle('')
      setEditorHtml('')
      setMainImageFile(null)
      setCategoryId('1')
      setPriority('عادي')
      setAddToTinker(false)
      setErrors({})
        onSave(data.data)
      } else {
        alert(data.error || 'Failed to save news')
      }
    } catch (error) {
      alert('Failed to save news: ' + error.message)
    } finally {
      setIsPublishing(false)
    }
  }

  const handleEditorChange = useCallback((editor) => {
    setEditorHtml(editor.getHtml())
    if (errors.content) setErrors(prev => ({ ...prev, content: '' }))
  }, [errors.content])

  const handleCreated = useCallback((editor) => { 
    setEditor(editor)
    
    // Add paste event listener for real-time YouTube URL conversion
    const editorDom = editor.getEditableContainer()
    if (editorDom) {
      editorDom.addEventListener('paste', (event) => {
        setTimeout(() => {
          const clipboardData = event.clipboardData || window.clipboardData;
          const pastedData = clipboardData.getData('text');
          
          // Check if pasted content is a YouTube URL
          const youtubeRegex = /(?:https?:\/\/)?(?:www\.)?(youtube\.com\/watch\?v=|youtu\.be\/)([a-zA-Z0-9_-]+)/;
          const match = pastedData.match(youtubeRegex);
          
          if (match) {
            // Get current HTML and replace YouTube URLs
            const currentHtml = editor.getHtml();
            const convertedHtml = convertYouTubeUrls(currentHtml);
            
            // Only update if conversion happened
            if (convertedHtml !== currentHtml) {
              editor.setHtml(convertedHtml);
            }
          }
        }, 100); // Small delay to ensure paste content is in editor
      })
    }
  }, [])

  useEffect(() => {
    return () => { 
      if (editor) { 
        editor.destroy()
        setEditor(null) 
      } 
    }
  }, [editor])

  // Add this useEffect for auto-save
  useEffect(() => {
    const formData = {
      title,
      categoryId,
      priority,
      addToTinker,
      editorHtml
    };
    if (title.trim() || editorHtml.trim()) {
      localStorage.setItem('addNewsFormDraft', JSON.stringify(formData));
    }
  }, [title, categoryId, priority, addToTinker, editorHtml]);

  // Add this useEffect for restoring data
  useEffect(() => {
    const savedDraft = localStorage.getItem('addNewsFormDraft');
    if (savedDraft) {
      const parsed = JSON.parse(savedDraft);
      setTitle(parsed.title || '');
      setCategoryId(parsed.categoryId || '');
      setPriority(parsed.priority || 'عادي');
      setAddToTinker(parsed.addToTinker || false);
      if (parsed.editorHtml) {
        setEditorHtml(parsed.editorHtml);
      }
    }
  }, []);

  const contentStats = React.useMemo(() => {
    const textLength = editorHtml.length
    const imageCount = (editorHtml.match(/<img/g) || []).length
    const youtubeCount = (editorHtml.match(/youtube\.com\/embed\//g) || []).length
    const hasLargeContent = textLength > 50000
    
    return {
      textLength,
      imageCount,
      youtubeCount,
      hasLargeContent,
      sizeKB: (textLength / 1024).toFixed(1)
    }
  }, [editorHtml])

  // Get selected category info for UI feedback
  const selectedCategoryInfo = categories.find(cat => cat.id === parseInt(categoryId))
  const isReviewCategory = categoryId === '4'

  return (
    <div>
      <div className="dashboard-page-header">
        <div className="dashboard-page-header-actions">
          {/* <button onClick={onBack} className="dashboard-back-btn">
            <svg className="dashboard-icon" viewBox="0 0 24 24" fill="none" stroke="currentColor"
              strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
              <path d="M19 12H5" /><path d="M12 19l-7-7 7-7" />
            </svg>
            العودة للأخبار
          </button> */}
        </div>
      </div>

      <form onSubmit={handleSubmit} className="dashboard-add-news-form" encType="multipart/form-data">
        <div className="dashboard-card">
          <div className="dashboard-card-header"><h3>إضافة خبر جديد</h3></div>
          <div className="dashboard-card-body">
            
            <div className="dashboard-form-group">
              <label htmlFor="title" className="dashboard-form-label">عنوان الخبر *</label>
              <input id="title" name="title" value={title}
                onChange={e => { setTitle(e.target.value); if (errors.title) setErrors(prev => ({ ...prev, title: '' })) }}
                className={`dashboard-form-input ${errors.title ? 'dashboard-error' : ''}`}
                placeholder="اكتب عنوان الخبر هنا..."
                style={{ textAlign: 'right', color: '#000000', backgroundColor: '#ffffff' }} />
              {errors.title && <span className="dashboard-error-message">{errors.title}</span>}
            </div>

            <div className="dashboard-form-group">
              {/* <label htmlFor="mainImage" className="dashboard-form-label">الصورة الرئيسية</label> */}
              {/* <input id="mainImage" name="mainImage" type="file" accept="image/*"
                onChange={e => setMainImageFile(e.target.files[0])}
                className="dashboard-form-input" style={{ textAlign: 'right' }} /> */}
            </div>

            <div className="dashboard-form-group">
              <label htmlFor="category" className="dashboard-form-label">فئة الخبر</label>
              {/* <select id="category" name="category" value={categoryId}
                onChange={e => setCategoryId(e.target.value)}
                className="dashboard-form-input"
                style={{ textAlign: 'right' }} disabled={loadingCategories}>
                <option value="">{loadingCategories ? 'جاري تحميل الفئات...' : 'اختر الفئة...'}</option>
                {categories.map(category => (
                  <option key={category.id} value={category.id}>{category.display_name}</option>
                ))}
              </select> */}

              <select
              id="category"
              name="category"
              value={categoryId}
              onChange={e => setCategoryId(e.target.value)}
              className="dashboard-form-input"
              style={{ textAlign: 'right' }}
              disabled={loadingCategories}
            >
              {loadingCategories && (
                <option value="">جاري تحميل الفئات...</option>
              )}
              {categories.map(category => (
                <option key={category.id} value={category.id}>
                  {category.display_name}
                </option>
              ))}
            </select>

              
              {/* Show publish status based on category */}
              {isReviewCategory && (
                <div style={{ 
                  marginTop: '8px', 
                  padding: '8px 12px', 
                  backgroundColor: '#fff3cd', 
                  border: '1px solid #ffeaa7', 
                  borderRadius: '4px',
                  fontSize: '0.9em',
                  textAlign: 'right'
                }}>
                  📝 سيتم حفظ الخبر للمراجعة ولن يتم نشره تلقائياً
                </div>
              )}
              {categoryId && categoryId !== '4' && (
                <div style={{ 
                  marginTop: '8px', 
                  padding: '8px 12px', 
                  backgroundColor: '#d1ecf1', 
                  border: '1px solid #74c0fc', 
                  borderRadius: '4px',
                  fontSize: '0.9em',
                  textAlign: 'right'
                }}>
                  ✅ سيتم نشر الخبر تلقائياً عند الحفظ
                </div>
              )}
            </div>

            <div className="dashboard-form-group">
              <label className="dashboard-form-label">أولوية الخبر</label>
              <select value={priority} onChange={e => setPriority(e.target.value)}
                className="dashboard-form-input" style={{ textAlign: 'right' }}>
                <option value="عاجل">عاجل</option>
                <option value="عادي">عادي</option>
              </select>
            </div>

            <div className="dashboard-form-group">
              <label className="dashboard-form-label" style={{ display: 'flex', alignItems: 'center', justifyContent: 'flex-start' }}>
                <input type="checkbox" checked={addToTinker} onChange={e => setAddToTinker(e.target.checked)}
                  style={{ marginLeft: '8px' }} />
                إضافة إلى شريط الأخبار
              </label>
            </div>

            <div className="dashboard-form-group" style={{ direction: 'rtl' }}>
              <label className="dashboard-form-label">محتوى الخبر * 
                <span style={{ fontSize: '0.9em', color: '#666', fontWeight: 'normal' }}>
                  (يمكنك لصق رابط يوتيوب مباشرة)
                </span>
              </label>
              
              {/* Show upload status */}
              {uploadingImages > 0 && (
                <div style={{ 
                  padding: '8px 12px', 
                  backgroundColor: '#e3f2fd', 
                  border: '1px solid #2196f3', 
                  borderRadius: '4px', 
                  marginBottom: '8px',
                  textAlign: 'center'
                }}>
                  جاري رفع {uploadingImages} صورة... يرجى الانتظار
                </div>
              )}
              
              <div style={{ border: '1px solid #ccc', zIndex: 100 }}>
                <Toolbar editor={editor} defaultConfig={toolbarConfig} mode="default"
                  style={{ borderBottom: '1px solid #ccc' }} />
                <Editor defaultConfig={editorConfig} value={editorHtml}
                  onCreated={handleCreated} onChange={handleEditorChange}
                  mode="default" style={{ minHeight: '300px' }} />
              </div>
              {errors.content && <span className="dashboard-error-message">{errors.content}</span>}
            </div>

            {/* Content stats */}
            <div style={{ 
              marginTop: '8px', 
              textAlign: 'right', 
              color: contentStats.hasLargeContent ? '#f44336' : '#555',
              fontSize: '0.9em'
            }}>
              حجم النص: {contentStats.sizeKB} KB | الصور: {contentStats.imageCount} | فيديوهات يوتيوب: {contentStats.youtubeCount}
              {contentStats.hasLargeContent && (
                <span style={{ display: 'block', color: '#f44336' }}>
                  ⚠️ المحتوى كبير جداً - قد يؤثر على الأداء
                </span>
              )}
            </div>

            <div className="dashboard-form-actions">
              <button 
                type="submit" 
                className="dashboard-btn dashboard-btn-primary"
                disabled={isPublishing || uploadingImages > 0}
              >
                {isPublishing ? 
                  (isReviewCategory ? 'جاري حفظ الخبر للمراجعة...' : 'جاري نشر الخبر...') : 
                 uploadingImages > 0 ? `رفع الصور (${uploadingImages})...` : 
                 isReviewCategory ? 'حفظ للمراجعة' : 'نشر الخبر'}
              </button>
            </div>
          </div>
        </div>
      </form>
    </div>
  )
}

export default AddNewsPage